<script setup>
/* global axios */
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import { providerMoney } from './model';
const props = defineProps({
  accountId: { type: [String, Number], required: true },
  provider: { type: Object, default: () => ({}) },
  disabled: Boolean,
});
const emit = defineEmits(['updated', 'configured']);
const keyDialog = ref(null);
const disconnectDialog = ref(null);
const apiKey = ref('');
const busy = ref(false);
const error = ref('');
const notice = ref('');
const now = ref(Date.now());
let pending = null;
let epoch = 0;
let requests = new AbortController();
let timer;
const api = () =>
  `/api/v1/accounts/${props.accountId}/tg_finance/providers/sexy_smm`;
const stale = computed(
  () =>
    props.provider.stale ||
    !props.provider.checked_at ||
    now.value - new Date(props.provider.checked_at).getTime() >= 900000
);
const checkedAt = computed(() =>
  props.provider.checked_at
    ? new Date(props.provider.checked_at).toLocaleString('ru-RU', {
        timeZone: 'Europe/Moscow',
      })
    : 'Ещё не обновлялся'
);
const providerError = computed(
  () =>
    ({
      sexy_smm_auth_failed: 'Сервис отклонил подключение. Проверьте API-ключ.',
      sexy_smm_credentials_invalid:
        'Не удалось прочитать сохранённый ключ. Подключите сервис заново.',
      sexy_smm_invalid_response:
        'Не удалось распознать ответ сервиса. Проверьте API-ключ или попробуйте позже.',
      sexy_smm_unavailable:
        'Сервис временно недоступен. Попробуйте обновить баланс позже.',
    })[props.provider.error_code] || ''
);
function clearKey() {
  apiKey.value = '';
  pending = null;
}
function openKey() {
  clearKey();
  error.value = '';
  notice.value = '';
  keyDialog.value?.open();
}
function reset() {
  epoch += 1;
  requests.abort();
  requests = new AbortController();
  clearKey();
  busy.value = false;
  error.value = '';
  notice.value = '';
  keyDialog.value?.close();
  disconnectDialog.value?.close();
}
async function readSnapshot() {
  const owner = String(props.accountId);
  const stamp = epoch;
  try {
    const response = await axios({
      method: 'get',
      url: api(),
      signal: requests.signal,
    });
    if (owner === String(props.accountId) && stamp === epoch)
      emit('updated', response.data);
  } catch {
    /* Keep the last snapshot; its age still shows that it is stale. */
  }
}
async function refresh() {
  if (busy.value || props.disabled) return;
  const owner = String(props.accountId);
  const stamp = epoch;
  busy.value = true;
  error.value = '';
  notice.value = '';
  try {
    const response = await axios({
      method: 'post',
      url: `${api()}/refresh`,
      data: {},
      signal: requests.signal,
    });
    if (owner !== String(props.accountId) || stamp !== epoch) return;
    emit('updated', response.data.provider);
    now.value = Date.now();
    notice.value = response.data.refreshed
      ? 'Проверка баланса завершена.'
      : 'Баланс уже проверялся недавно. Повторная проверка доступна через минуту.';
  } catch {
    if (owner === String(props.accountId) && stamp === epoch)
      error.value =
        'Не удалось обновить данные. Сохранён последний известный баланс.';
  } finally {
    if (stamp === epoch) busy.value = false;
  }
}
async function configure() {
  if (busy.value || props.disabled || !apiKey.value.trim()) return;
  const owner = String(props.accountId);
  const stamp = epoch;
  if (!pending || pending.api_key !== apiKey.value.trim())
    pending = {
      request_key: crypto.randomUUID(),
      api_key: apiKey.value.trim(),
    };
  busy.value = true;
  error.value = '';
  notice.value = '';
  try {
    const response = await axios({
      method: 'post',
      url: `${api()}/configure`,
      data: { ...pending },
      signal: requests.signal,
    });
    if (owner !== String(props.accountId) || stamp !== epoch) return;
    emit('updated', response.data);
    clearKey();
    keyDialog.value?.close();
    // Saving the key explicitly authorizes this first read-only balance check.
    const balance = await axios({
      method: 'post',
      url: `${api()}/refresh`,
      data: {},
      signal: requests.signal,
    });
    if (owner !== String(props.accountId) || stamp !== epoch) return;
    emit('updated', balance.data.provider);
    emit('configured');
    now.value = Date.now();
    notice.value = 'Подключение сохранено.';
  } catch (e) {
    if (owner !== String(props.accountId) || stamp !== epoch) return;
    error.value =
      e?.response?.data?.error === 'invalid_provider_key'
        ? 'Проверьте API-ключ: допустимы латинские буквы, цифры, дефис и подчёркивание, от 8 до 256 символов.'
        : 'Не удалось подтвердить сохранение или обновление. Повторите запрос либо обновите страницу: ключ мог сохраниться.';
  } finally {
    if (stamp === epoch) busy.value = false;
  }
}
async function disconnect() {
  if (busy.value || props.disabled) return;
  const owner = String(props.accountId);
  const stamp = epoch;
  busy.value = true;
  error.value = '';
  notice.value = '';
  try {
    const response = await axios({
      method: 'post',
      url: `${api()}/disconnect`,
      data: { request_key: crypto.randomUUID() },
      signal: requests.signal,
    });
    if (owner !== String(props.accountId) || stamp !== epoch) return;
    emit('updated', response.data);
    disconnectDialog.value?.close();
    clearKey();
    notice.value = 'Получение баланса отключено. Записи учёта сохранены.';
  } catch {
    if (stamp === epoch)
      error.value =
        'Не удалось отключить подключение. Обновите страницу и проверьте состояние.';
  } finally {
    if (stamp === epoch) busy.value = false;
  }
}
watch(() => props.accountId, reset);
onMounted(() => {
  timer = setInterval(() => {
    now.value = Date.now();
    if (props.provider.configured && !busy.value && !document.hidden)
      readSnapshot();
  }, 60000);
});
onBeforeUnmount(() => {
  clearInterval(timer);
  reset();
});
</script>

<template>
  <article
    class="p-4 rounded-xl border border-n-weak min-w-0 flex flex-col gap-3"
    aria-label="Баланс SexySMM"
  >
    <h3 class="text-sm font-medium text-n-slate-12 m-0">SexySMM</h3>
    <template v-if="provider.configured">
      <p class="text-lg text-n-slate-12 m-0">
        {{
          provider.balance == null
            ? 'Баланс ещё не получен'
            : providerMoney(provider.balance)
        }}
      </p>
      <p class="text-sm text-n-slate-11 m-0">
        По API сервиса. Обновлено: {{ checkedAt }} (МСК). Автоматическая
        проверка — раз в 15 минут.
      </p>
      <p v-if="stale" class="text-sm text-n-amber-11 m-0">
        {{
          provider.balance == null
            ? 'Ожидается успешное получение баланса.'
            : 'Данные устарели. Показан последний известный баланс.'
        }}
      </p>
      <Banner v-if="providerError" color="amber">{{ providerError }}</Banner>
      <div class="flex flex-wrap gap-2">
        <Button
          label="Обновить баланс SexySMM"
          :is-loading="busy"
          :disabled="busy || disabled"
          @click="refresh"
        />
        <Button
          label="Изменить API-ключ"
          slate
          faded
          :disabled="busy || disabled"
          @click="openKey"
        />
        <Button
          label="Отключить SexySMM"
          slate
          ghost
          :disabled="busy || disabled"
          @click="disconnectDialog?.open()"
        />
      </div>
    </template>
    <template v-else>
      <p class="text-sm text-n-slate-11 m-0">
        Подключите сервис, чтобы видеть его фактический баланс рядом с UNU.
      </p>
      <Button
        label="Подключить SexySMM"
        slate
        faded
        :disabled="busy || disabled"
        @click="openKey"
      />
    </template>
    <p class="text-sm text-n-slate-11 m-0">
      Баланс сервиса показан отдельно от движения по счетам. Пополнения и
      расходы учитываются отдельными операциями.
    </p>
    <Banner v-if="notice" color="slate" role="status">{{ notice }}</Banner>
    <Banner v-if="error" color="ruby" role="alert">{{ error }}</Banner>
    <Dialog
      ref="keyDialog"
      title="Подключение SexySMM"
      confirm-button-label="Сохранить"
      cancel-button-label="Отмена"
      :is-loading="busy"
      :disable-confirm-button="busy || disabled || !apiKey.trim()"
      @confirm="configure"
      @close="clearKey"
    >
      <p class="text-sm text-n-slate-11 m-0">
        Введите API-ключ из настроек SexySMM. Он хранится зашифрованным и
        используется для получения баланса.
      </p>
      <a
        href="https://sexy-smm.ru/developer/index"
        target="_blank"
        rel="noopener noreferrer"
        class="text-sm text-n-blue-11 underline"
        >Где взять API-ключ</a
      >
      <Input
        v-model="apiKey"
        type="password"
        label="API-ключ SexySMM"
        autocomplete="off"
        :disabled="busy"
      />
      <Banner v-if="error" color="ruby" role="alert">{{ error }}</Banner>
    </Dialog>
    <Dialog
      ref="disconnectDialog"
      title="Отключить SexySMM?"
      description="Ключ будет удалён из подключения. Счёт и записи учёта сохранятся."
      confirm-button-label="Отключить"
      cancel-button-label="Отмена"
      :is-loading="busy"
      :disable-confirm-button="busy || disabled"
      @confirm="disconnect"
    />
  </article>
</template>
